import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api';
import type { Quote, RateShopResult } from '../types';
import { RateTable } from './RateTable';
import { money, plural, shortDate, weight } from '../lib/format';
import type { EmbedContext } from '../lib/embedParams';

/**
 * Carrier options only - the view D365 embeds.
 *
 * The host already knows the load and shows it on the form, so there is no load
 * rail, no load detail panel and no masthead. The frame is opened *to* rate
 * shop, so it rates on mount rather than waiting for a click.
 *
 * It talks back to the host with postMessage so X++ can capture the chosen
 * carrier and write it to the load. See docs/EMBEDDING.md for the contract.
 */

const CHANNEL = 'ratelinx-rateshop';

interface Props {
  context: EmbedContext;
}

export function EmbedView({ context }: Props) {
  const { load, resolution, requestedLoadId, bare, selectOnly } = context;

  const [result, setResult] = useState<RateShopResult | null>(null);
  const [rating, setRating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busyShipVia, setBusyShipVia] = useState<string | null>(null);
  const [booked, setBooked] = useState<{ shipVia: string; trackingNumber: string } | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * Where to post. Defaults to '*' because the D365 origin is not known ahead
   * of time and nothing here is sensitive. Pass ?parentOrigin=https://... to
   * pin it, which you should once the environment URL is settled.
   */
  const parentOrigin = useMemo(() => {
    const p = new URLSearchParams(window.location.search).get('parentOrigin');
    return p && /^https?:\/\//.test(p) ? p : '*';
  }, []);

  const post = useCallback(
    (type: string, payload: Record<string, unknown> = {}) => {
      if (window.parent === window) return; // not framed
      try {
        window.parent.postMessage({ source: CHANNEL, type, loadId: load.loadId, ...payload }, parentOrigin);
      } catch {
        /* a pinned origin that does not match must not break the UI */
      }
    },
    [load.loadId, parentOrigin],
  );

  // --- Rate on mount --------------------------------------------------------
  const runRateShop = useCallback(async () => {
    setRating(true);
    setError(null);
    try {
      // A load built from URL parameters does not exist server-side, so it is
      // rated by value rather than by id.
      const r =
        resolution === 'parameters' && api.rateShopLoad
          ? await api.rateShopLoad(load)
          : await api.rateShop(load.loadId);
      setResult(r);
      post('rated', {
        quoteCount: r.quoteCount,
        recommended: r.recommendedShipVia,
        rateSource: r.rateSource,
        cheapest: r.analysis.cheapestShipVia,
        fastest: r.analysis.fastestShipVia,
      });
    } catch (err) {
      const message = (err as Error).message;
      setError(message);
      post('error', { message });
    } finally {
      setRating(false);
    }
  }, [load, resolution, post]);

  useEffect(() => {
    void runRateShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Tell the host how tall we are ---------------------------------------
  // D365 gives the iframe a fixed height; without this the table scrolls inside
  // a short frame instead of the host growing to fit.
  useEffect(() => {
    post('ready', { resolution });
    const el = rootRef.current;
    if (!el) return;

    let last = 0;
    const report = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h && Math.abs(h - last) > 2) {
        last = h;
        post('height', { height: h });
      }
    };
    report();

    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [post, resolution]);

  // --- Actions --------------------------------------------------------------
  const handleSelect = useCallback(
    async (q: Quote) => {
      setBusyShipVia(q.shipVia);
      setChosen(q.shipVia);
      post('selected', {
        shipVia: q.shipVia,
        serviceName: q.serviceName,
        carrier: q.carrier,
        scac: q.scac,
        rate: q.rate,
        currency: 'USD',
        deliveryDate: q.deliveryDate,
        transitDays: q.transitDays,
        commitTime: q.commitTime,
        meetsDeadline: q.meetsDeadline,
      });
      setBusyShipVia(null);
    },
    [post],
  );

  const handleBook = useCallback(
    async (q: Quote) => {
      setBusyShipVia(q.shipVia);
      try {
        const quote = {
          shipVia: q.shipVia,
          serviceName: q.serviceName,
          rate: q.rate,
          deliveryDate: q.deliveryDate,
          transitDays: q.transitDays,
        };
        // A parameter-built load has no server-side record to look up.
        const r =
          resolution === 'parameters' && api.bookLoad
            ? await api.bookLoad(load, quote)
            : await api.book(load.loadId, quote);
        setChosen(q.shipVia);
        setBooked({ shipVia: q.shipVia, trackingNumber: r.booking.trackingNumber });
        post('booked', {
          shipVia: q.shipVia,
          serviceName: q.serviceName,
          rate: q.rate,
          deliveryDate: q.deliveryDate,
          transitDays: q.transitDays,
          trackingNumber: r.booking.trackingNumber,
          shipmentId: r.booking.shipmentId,
        });
      } catch (err) {
        const message = (err as Error).message;
        setError(message);
        post('error', { message });
      } finally {
        setBusyShipVia(null);
      }
    },
    [load, resolution, post],
  );

  // --- The headline ---------------------------------------------------------
  const insight = useMemo(() => {
    if (!result || result.quotes.length < 2) return null;
    const { analysis } = result;
    const cheapest = result.quotes.find((q) => q.shipVia === analysis.cheapestShipVia);
    const fastest = result.quotes.find((q) => q.shipVia === analysis.fastestShipVia);
    if (!cheapest || !fastest) return null;

    const cheapestOverall = result.quotes.find((q) => q.shipVia === analysis.cheapestOverallShipVia);
    if (analysis.deadlinePremium && cheapestOverall && cheapestOverall !== cheapest) {
      return (
        <>
          <strong>{cheapestOverall.serviceName}</strong> at {money(cheapestOverall.rate)} is cheapest
          but misses the {shortDate(result.requiredByDate)} deadline. The cheapest that makes it is{' '}
          <strong>{cheapest.serviceName}</strong> at {money(cheapest.rate)} — this delivery date
          costs {money(analysis.deadlinePremium)}.
        </>
      );
    }
    if (
      analysis.cheapestTiesFastestOnDate &&
      cheapest !== fastest &&
      analysis.premiumOverCheapest !== null &&
      analysis.premiumOverCheapest >= 1
    ) {
      return (
        <>
          <strong>{cheapest.serviceName}</strong> arrives the same day as{' '}
          <strong>{fastest.serviceName}</strong> for {money(analysis.premiumOverCheapest)} less.
        </>
      );
    }
    if (analysis.dominatedCount > 0) {
      return (
        <>
          <strong>{plural(analysis.dominatedCount, 'service')}</strong> cost more <em>and</em> arrive
          later than {cheapest.serviceName}.
        </>
      );
    }
    return null;
  }, [result]);

  return (
    <div className="embed" ref={rootRef}>
      {!bare && (
        <div className="embed-strip">
          <div className="embed-strip-main">
            <span className="embed-load">{requestedLoadId ?? load.loadId}</span>
            {load.warehouse.zip && load.shipTo.zip && (
              <span className="embed-lane">
                {load.warehouse.state} {load.warehouse.zip} → {load.shipTo.state} {load.shipTo.zip}
              </span>
            )}
            <span className="embed-meta">{weight(load.totalWeight)}</span>
            <span className="embed-meta">need by {shortDate(load.requiredByDate)}</span>
            {resolution === 'demo-lane' && (
              <span
                className="chip"
                title={`No shipment detail was passed for ${requestedLoadId}, so a demo lane (${load.loadId}) is being priced. The lane and weight shown are what was rated. Pass toZip and weight to rate the real shipment.`}
              >
                demo lane
              </span>
            )}
          </div>
          <div className="posture">
            {result && (
              <span
                className={`chip ${result.rateSource === 'recorded' ? 'accent' : 'warning'}`}
                title={
                  result.rateSource === 'recorded'
                    ? 'A real carrier response, captured earlier and replayed'
                    : 'Generated by the fallback rating model — not carrier data'
                }
              >
                <span className="dot" aria-hidden="true" />
                {result.rateSource === 'recorded' ? 'Recorded' : 'Modelled'}
              </span>
            )}
            <button type="button" className="btn small" onClick={runRateShop} disabled={rating}>
              {rating && <span className="spinner" />}
              Re-shop
            </button>
          </div>
        </div>
      )}

      {resolution === 'fallback' && (
        <div className="callout warn">
          <span className="icon" aria-hidden="true">!</span>
          <span>
            No load was passed, so the sample lane {load.loadId} is shown. Append{' '}
            <code>?loadId=…</code> — and <code>toZip</code> plus <code>weight</code> to rate the
            real shipment.
          </span>
        </div>
      )}

      {error && (
        <div className="callout danger">
          <span className="icon" aria-hidden="true">!</span>
          <span>{error}</span>
        </div>
      )}

      {rating && !result && (
        <div className="empty">
          <span className="spinner" />
          Rating {requestedLoadId ?? load.loadId} across every eligible service…
        </div>
      )}

      {result && result.quotes.length > 0 && (
        <>
          {insight && (
            <div className="callout insight">
              <span className="icon" aria-hidden="true">→</span>
              <span>{insight}</span>
            </div>
          )}

          {result.analysis.missesDeadlineCount > 0 && (
            <div className="callout danger">
              <span className="icon" aria-hidden="true">!</span>
              <span>
                {plural(result.analysis.missesDeadlineCount, 'service')} deliver after{' '}
                {shortDate(result.requiredByDate)} and cannot be used.
              </span>
            </div>
          )}

          {booked && (
            <div className="callout insight">
              <span className="icon" aria-hidden="true">✓</span>
              <span>
                Booked <strong>{booked.shipVia}</strong> · tracking{' '}
                <code>{booked.trackingNumber}</code>
              </span>
            </div>
          )}

          <RateTable
            result={result}
            selectedShipVia={chosen}
            busyShipVia={busyShipVia}
            onSelect={handleSelect}
            onBook={handleBook}
            canBook={!selectOnly}
          />
        </>
      )}

      {result && result.quotes.length === 0 && (
        <div className="empty">No priced services came back for this lane.</div>
      )}
    </div>
  );
}
